/**
 * VistoeraTASK-00598 — pure shape-brush geometry (paths + aspect lock).
 * Shares create-drag math with crop via cropGeometry.rectFromCreateDrag.
 */

import { isAspectLocked as cropAspectLocked, rectFromCreateDrag } from './cropGeometry'

export type ShapeBrushType =
  | 'rect'
  | 'square'
  | 'ellipse'
  | 'circle'
  | 'star'
  | 'hexagon'
  | 'triangle'

export interface ShapeAnnotation {
  id: string
  type: ShapeBrushType
  x: number
  y: number
  w: number
  h: number
  strokeColor: string
  strokeWidth: number
  fillEnabled: boolean
  fillColor: string
  fillOpacity: number
}

/** Preset swatches used by shape stroke/fill and crop border (compact UI). */
export const EDITOR_PRESET_COLORS = [
  '#ff4d4f',
  '#fa8c16',
  '#fadb14',
  '#52c41a',
  '#13c2c2',
  '#1677ff',
  '#2f54eb',
  '#722ed1',
  '#ffffff',
] as const

/** Square/circle/star/hex/triangle keep a regular 1:1 bbox. */
export function shapeBrushAlwaysSquare(type: ShapeBrushType): boolean {
  return type === 'square' || type === 'circle' || type === 'star' || type === 'hexagon' || type === 'triangle'
}

export function geomFromShapeDrag(
  type: ShapeBrushType,
  start: { x: number; y: number },
  pointer: { x: number; y: number },
  shiftHeld: boolean,
): { x: number; y: number; w: number; h: number } {
  const locked =
    shapeBrushAlwaysSquare(type) ||
    ((type === 'rect' || type === 'ellipse') && cropAspectLocked(type, shiftHeld))
  return rectFromCreateDrag(start, pointer, locked)
}

/** Build a closed path for the shape inside its axis-aligned bbox. */
export function buildShapePath(
  ctx: CanvasRenderingContext2D,
  type: ShapeBrushType,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const cx = x + w / 2
  const cy = y + h / 2
  const rx = Math.max(0, w / 2)
  const ry = Math.max(0, h / 2)
  ctx.beginPath()
  if (type === 'rect' || type === 'square') {
    ctx.rect(x, y, w, h)
    return
  }
  if (type === 'ellipse' || type === 'circle') {
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2)
    return
  }
  if (type === 'star') {
    const outerR = Math.min(rx, ry)
    const innerR = outerR * 0.382
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? outerR : innerR
      const a = -Math.PI / 2 + (i * Math.PI) / 5
      const px = cx + Math.cos(a) * r
      const py = cy + Math.sin(a) * r
      if (i === 0) ctx.moveTo(px, py)
      else ctx.lineTo(px, py)
    }
    ctx.closePath()
    return
  }
  if (type === 'hexagon') {
    const r = Math.min(rx, ry)
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 3
      const px = cx + Math.cos(a) * r
      const py = cy + Math.sin(a) * r
      if (i === 0) ctx.moveTo(px, py)
      else ctx.lineTo(px, py)
    }
    ctx.closePath()
    return
  }
  // equilateral triangle pointing up, inscribed in bbox
  const r = Math.min(rx, ry)
  for (let i = 0; i < 3; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / 3
    const px = cx + Math.cos(a) * r
    const py = cy + Math.sin(a) * r
    if (i === 0) ctx.moveTo(px, py)
    else ctx.lineTo(px, py)
  }
  ctx.closePath()
}

export function drawShapeAnnotation(ctx: CanvasRenderingContext2D, ann: ShapeAnnotation): void {
  if (ann.w < 1 || ann.h < 1) return
  const strokeW = Math.max(0.5, Number.isFinite(ann.strokeWidth) ? ann.strokeWidth : 1)
  buildShapePath(ctx, ann.type, ann.x, ann.y, ann.w, ann.h)
  if (ann.fillEnabled) {
    const op = Math.min(1, Math.max(0, Number.isFinite(ann.fillOpacity) ? ann.fillOpacity : 0))
    ctx.save()
    ctx.globalAlpha = op
    ctx.fillStyle = ann.fillColor || '#ff4d4f'
    ctx.fill()
    ctx.restore()
    // rebuild path after fill (some browsers consume it)
    buildShapePath(ctx, ann.type, ann.x, ann.y, ann.w, ann.h)
  }
  ctx.save()
  ctx.strokeStyle = ann.strokeColor || '#ff4d4f'
  ctx.lineWidth = strokeW
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.stroke()
  ctx.restore()
}

export function drawShapeAnnotations(ctx: CanvasRenderingContext2D, list: ShapeAnnotation[]): void {
  for (const ann of list) drawShapeAnnotation(ctx, ann)
}

/**
 * Interaction-only selection halo (VistoeraTASK-00609).
 * Call from preview redraws only — never from export compositors.
 */
export function drawShapeSelectionHighlight(ctx: CanvasRenderingContext2D, ann: ShapeAnnotation): void {
  if (ann.w < 1 || ann.h < 1) return
  const strokeW = Math.max(0.5, Number.isFinite(ann.strokeWidth) ? ann.strokeWidth : 1)
  buildShapePath(ctx, ann.type, ann.x, ann.y, ann.w, ann.h)
  ctx.save()
  ctx.strokeStyle = 'rgba(99, 102, 241, 0.95)'
  ctx.lineWidth = strokeW + 3
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.setLineDash([])
  ctx.stroke()
  ctx.restore()
}

/** Rotate annotation bbox 90° clockwise within image of size (iw, ih). */
export function rotateAnnotationClockwise90(
  ann: ShapeAnnotation,
  iw: number,
  ih: number,
): ShapeAnnotation {
  // iw retained for call-site clarity (pre-rotation width); 90° CW maps via ih.
  void iw
  const { x, y, w, h } = ann
  return {
    ...ann,
    x: ih - (y + h),
    y: x,
    w: h,
    h: w,
  }
}

export function scaleAnnotation(
  ann: ShapeAnnotation,
  scaleX: number,
  scaleY: number,
): ShapeAnnotation {
  return {
    ...ann,
    x: ann.x * scaleX,
    y: ann.y * scaleY,
    w: ann.w * scaleX,
    h: ann.h * scaleY,
    strokeWidth: ann.strokeWidth * ((scaleX + scaleY) / 2),
  }
}

export function cloneAnnotations(list: ShapeAnnotation[] | null | undefined): ShapeAnnotation[] {
  if (!list || list.length === 0) return []
  return list.map((a) => ({ ...a }))
}

/** Screen-stable grab radius → image pixels (VistoeraTASK-00607). */
export function shapeStrokeHitToleranceImgPx(strokeWidth: number, scale: number): number {
  const s = Number.isFinite(scale) && scale > 0 ? scale : 0.0001
  const screenTol = 6
  const strokeHalf = Math.max(0.5, Number.isFinite(strokeWidth) ? strokeWidth : 1) / 2
  return Math.max(strokeHalf + 2, screenTol / s)
}

function distPointToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1
  const dy = y2 - y1
  const len2 = dx * dx + dy * dy
  if (len2 <= 1e-12) return Math.hypot(px - x1, py - y1)
  let t = ((px - x1) * dx + (py - y1) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
}

function pointNearPolyline(
  p: { x: number; y: number },
  pts: ReadonlyArray<{ x: number; y: number }>,
  tol: number,
  closed: boolean,
): boolean {
  if (pts.length < 2) return false
  for (let i = 0; i < pts.length - 1; i++) {
    if (distPointToSegment(p.x, p.y, pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y) <= tol) {
      return true
    }
  }
  if (closed && pts.length >= 3) {
    const a = pts[pts.length - 1]
    const b = pts[0]
    if (distPointToSegment(p.x, p.y, a.x, a.y, b.x, b.y) <= tol) return true
  }
  return false
}

/** Outline vertices for polygon-like shapes (same geometry as buildShapePath). */
export function shapeOutlinePoints(
  type: ShapeBrushType,
  x: number,
  y: number,
  w: number,
  h: number,
): { x: number; y: number }[] {
  const cx = x + w / 2
  const cy = y + h / 2
  const rx = Math.max(0, w / 2)
  const ry = Math.max(0, h / 2)
  if (type === 'rect' || type === 'square') {
    return [
      { x, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
    ]
  }
  if (type === 'star') {
    const outerR = Math.min(rx, ry)
    const innerR = outerR * 0.382
    const pts: { x: number; y: number }[] = []
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? outerR : innerR
      const a = -Math.PI / 2 + (i * Math.PI) / 5
      pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r })
    }
    return pts
  }
  if (type === 'hexagon') {
    const r = Math.min(rx, ry)
    const pts: { x: number; y: number }[] = []
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 3
      pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r })
    }
    return pts
  }
  if (type === 'triangle') {
    const r = Math.min(rx, ry)
    const pts: { x: number; y: number }[] = []
    for (let i = 0; i < 3; i++) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / 3
      pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r })
    }
    return pts
  }
  return []
}

function pointInPolygon(p: { x: number; y: number }, pts: ReadonlyArray<{ x: number; y: number }>): boolean {
  // Ray casting
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x
    const yi = pts[i].y
    const xj = pts[j].x
    const yj = pts[j].y
    if (yi === yj) continue
    const intersect = yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

/**
 * Stroke-near (and optional fill) hit-test in IMAGE pixels.
 * Hollow shapes: only the outline band is movable — not the empty interior.
 */
export function hitTestShapeAnnotation(
  p: { x: number; y: number },
  ann: ShapeAnnotation,
  toleranceImgPx: number,
): boolean {
  if (ann.w < 1 || ann.h < 1) return false
  const tol = Math.max(0.5, Number.isFinite(toleranceImgPx) ? toleranceImgPx : 4)
  const { type, x, y, w, h } = ann
  const cx = x + w / 2
  const cy = y + h / 2
  const rx = Math.max(1e-6, w / 2)
  const ry = Math.max(1e-6, h / 2)

  if (type === 'ellipse' || type === 'circle') {
    const nx = (p.x - cx) / rx
    const ny = (p.y - cy) / ry
    const er = Math.hypot(nx, ny)
    if (ann.fillEnabled && er <= 1) return true
    if (er < 1e-9) return false
    const onX = cx + (nx / er) * rx
    const onY = cy + (ny / er) * ry
    return Math.hypot(p.x - onX, p.y - onY) <= tol
  }

  const pts = shapeOutlinePoints(type, x, y, w, h)
  if (pts.length === 0) return false
  if (pointNearPolyline(p, pts, tol, true)) return true
  if (ann.fillEnabled && pointInPolygon(p, pts)) return true
  return false
}

/** Top-most (last drawn) annotation under the pointer, or null. */
export function hitTestTopShapeAnnotation(
  p: { x: number; y: number },
  list: readonly ShapeAnnotation[],
  toleranceImgPx: number,
): ShapeAnnotation | null {
  for (let i = list.length - 1; i >= 0; i--) {
    if (hitTestShapeAnnotation(p, list[i], toleranceImgPx)) return list[i]
  }
  return null
}

/** Translate annotation by image-space delta (size/style unchanged). */
export function translateShapeAnnotation(
  ann: ShapeAnnotation,
  dx: number,
  dy: number,
): ShapeAnnotation {
  return { ...ann, x: ann.x + dx, y: ann.y + dy }
}
